import React, { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { ArrowRight, CheckCircle2, Star, TrendingUp, Zap, Shield } from "lucide-react";

/* ─── STYLES ──────────────────────────────────────────────────────────────── */
const STYLES = `
  /* Barlow Condensed — tipografía hero */
  .ch-big {
    font-family: 'Barlow Condensed', 'Barlow', Impact, 'Arial Narrow', sans-serif;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.025em;
    line-height: 0.88;
  }

  /* Línea stroke / outline */
  .ch-outline {
    -webkit-text-stroke: 2px rgba(255,255,255,0.30);
    color: transparent;
    font-style: italic;
  }

  /* Glow de acento — cambia con cada slide */
  .ch-accent-glow {
    transition: color 0.6s ease, -webkit-text-stroke-color 0.6s ease;
  }

  /* Fondo grid sutil */
  .ch-grid-bg {
    background-size: 60px 60px;
    background-image:
      linear-gradient(to right,  rgba(255,255,255,0.04) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
    mask-image: radial-gradient(ellipse 90% 70% at 50% 0%, black, transparent);
    -webkit-mask-image: radial-gradient(ellipse 90% 70% at 50% 0%, black, transparent);
  }

  /* Progress bar del slide */
  @keyframes ch-progress {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  .ch-progress-bar {
    transform-origin: left;
    animation: ch-progress var(--ch-duration, 4s) linear forwards;
  }

  /* Float sutil del mockup */
  @keyframes ch-float {
    0%, 100% { transform: translateY(0px);   }
    50%       { transform: translateY(-10px); }
  }
  .ch-float { animation: ch-float 5s ease-in-out infinite; }

  /* Shimmer en números */
  @keyframes ch-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  .ch-shimmer-text {
    background: linear-gradient(90deg, #ff6b35 0%, #ffaa44 50%, #ff6b35 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ch-shimmer 3s linear infinite;
  }

  /* Pulse badge */
  @keyframes ch-pulse {
    0%, 100% { opacity:1; transform:scale(1);    }
    50%       { opacity:.6; transform:scale(1.2); }
  }
  .ch-pulse { animation: ch-pulse 2s ease-in-out infinite; }

  /* Slide transition */
  .ch-slide-enter {
    animation: ch-slide-in 0.55s cubic-bezier(0.22,1,0.36,1) forwards;
  }
  @keyframes ch-slide-in {
    from { opacity:0; transform: translateY(22px); }
    to   { opacity:1; transform: translateY(0);    }
  }
  .ch-slide-exit {
    animation: ch-slide-out 0.35s ease-in forwards;
  }
  @keyframes ch-slide-out {
    from { opacity:1; transform: translateY(0);     }
    to   { opacity:0; transform: translateY(-18px); }
  }

  /* Mockup shadow */
  .ch-mockup {
    box-shadow:
      0 0 0 1px rgba(255,107,53,0.12),
      0 30px 70px rgba(0,0,0,0.65),
      0 0 60px rgba(255,107,53,0.07);
  }
`;

/* ─── SLIDES DATA ─────────────────────────────────────────────────────────── */
const SLIDES = [
  {
    id: 1,
    line1:    "Tu obra.",
    line2:    "Sin el caos.",
    line2cls: "ch-outline",
    accent:   "#FF6B35",
    sub:      "El software que muestra el margen real de cada obra en tiempo real. Sin Excel. Sin adivinar. Sin sorpresas.",
    stat:     null,
    mockup: {
      badge:    { text: "Presupuesto aprobado", sub: "Reforma Les Corts · hoy", icon: "✓" },
      metric:   { val: "€48.2k", label: "Reforma Eixample L3", status: "En plazo", color: "#10B981" },
      alert:    null,
    },
  },
  {
    id: 2,
    line1:    "127",
    line2:    "Proyectos.",
    line2cls: "ch-outline",
    accent:   "#3B82F6",
    sub:      "Centraliza presupuestos, equipos, materiales y plazos en una sola plataforma. Menos reuniones, más obra entregada.",
    stat:     "proyectos activos gestionados con MANDO",
    mockup: {
      badge:    { text: "Obra Nueva Gràcia", sub: "Adelantada · €127k", icon: "↑" },
      metric:   { val: "+38%", label: "Margen medio", status: "Esta semana", color: "#10B981" },
      alert:    null,
    },
  },
  {
    id: 3,
    line1:    "Margen real.",
    line2:    "Al instante.",
    line2cls: "ch-outline",
    accent:   "#10B981",
    sub:      "MANDO calcula la rentabilidad de cada obra en tiempo real. Sabes exactamente cuánto ganas antes de que sea tarde para reaccionar.",
    stat:     null,
    mockup: {
      badge:    { text: "Factura cobrada", sub: "Rehabilitació Sarrià · €22.5k", icon: "€" },
      metric:   { val: "34.2%", label: "Margen medio", status: "Últimos 30 días", color: "#10B981" },
      alert:    { text: "Equipo notificado", sub: "4 operarios · Eixample" },
    },
  },
  {
    id: 4,
    line1:    "Total",
    line2:    "Control.",
    line2cls: "",
    accent:   "#FF6B35",
    sub:      "Toma el control de tu obra hoy. 15 días gratis, sin tarjeta, sin permanencia. Lista en menos de 2 horas.",
    stat:     null,
    mockup: {
      badge:    { text: "IA MANDO activa", sub: "Conectado a tus datos", icon: "★" },
      metric:   { val: "4.9/5", label: "Valoración media", status: "120+ reseñas verificadas", color: "#FF6B35" },
      alert:    { text: "+320 empresas confían", sub: "en MANDO · Barcelona" },
    },
  },
];

const DURATION = 4200; // ms por slide

/* ─── MOCKUP COMPONENT ────────────────────────────────────────────────────── */
function AppMockup({ slide }) {
  const { badge, metric, alert } = slide.mockup;
  return (
    <div className="relative ch-float">
      {/* Glow halo */}
      <div className="absolute -inset-6 rounded-3xl pointer-events-none"
           style={{ background: `radial-gradient(ellipse at center, ${slide.accent}18, transparent 70%)` }} />

      {/* Phone frame */}
      <div className="relative mx-auto w-[240px] sm:w-[270px]">

        {/* Top notification badge */}
        <div className="absolute -top-5 -left-8 z-30 bg-slate-800/95 border border-slate-700
                        rounded-2xl px-4 py-2.5 shadow-xl backdrop-blur-xl
                        flex items-center gap-3 min-w-[220px]
                        transition-all duration-500">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
               style={{ background: slide.accent }}>
            {badge.icon}
          </div>
          <div>
            <div className="text-white text-xs font-semibold leading-tight">{badge.text}</div>
            <div className="text-slate-400 text-[10px] mt-0.5">{badge.sub}</div>
          </div>
        </div>

        {/* Phone body */}
        <div className="rounded-[2.5rem] bg-[#0a0f1e] border-[6px] border-slate-700/80 ch-mockup overflow-hidden">
          {/* Notch */}
          <div className="flex justify-center py-2.5">
            <div className="w-20 h-1.5 rounded-full bg-slate-700" />
          </div>

          {/* Screen content */}
          <div className="px-4 pb-6 space-y-3 min-h-[340px]">

            {/* Header row */}
            <div className="flex items-center justify-between py-1">
              <span className="text-slate-400 text-xs">MANDO</span>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                   style={{ background: slide.accent }}>
                RS
              </div>
            </div>

            {/* Big circle metric */}
            <div className="flex flex-col items-center py-3">
              <div className="relative w-28 h-28">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#1e293b" strokeWidth="8" />
                  <circle cx="50" cy="50" r="40" fill="none" strokeWidth="8"
                          stroke={slide.accent}
                          strokeLinecap="round"
                          strokeDasharray="251"
                          strokeDashoffset="63"
                          style={{ transition: "stroke 0.6s ease, stroke-dashoffset 0.8s ease" }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-white font-black text-2xl leading-none">127</span>
                  <span className="text-slate-400 text-[9px] uppercase tracking-wider mt-0.5">Proyectos</span>
                </div>
              </div>
            </div>

            {/* Obra rows */}
            {[
              { name: "Reforma Eixample L3",  val: "€48.2k",  color: "#10B981", icon: "●" },
              { name: "Obra Nueva Gràcia",    val: "+€127k",  color: slide.accent, icon: "↑" },
              { name: "Rehabilitació Sarrià", val: "€22.5k",  color: "#94A3B8", icon: "○" },
            ].map(row => (
              <div key={row.name}
                   className="flex items-center gap-2.5 bg-slate-800/50 rounded-xl px-3 py-2.5
                              border border-slate-700/40">
                <span className="text-sm" style={{ color: row.color }}>{row.icon}</span>
                <span className="text-white text-xs flex-1 truncate">{row.name}</span>
                <span className="text-xs font-bold" style={{ color: row.color }}>{row.val}</span>
              </div>
            ))}

            {/* Home bar */}
            <div className="flex justify-center pt-2">
              <div className="w-24 h-1 rounded-full bg-slate-600" />
            </div>
          </div>
        </div>

        {/* Bottom right floating badge — metric */}
        <div className="absolute -bottom-5 -right-8 z-30 bg-slate-900/95 border border-slate-700
                        rounded-2xl px-4 py-2.5 shadow-xl backdrop-blur-xl text-right
                        transition-all duration-500">
          <div className="text-sm font-black" style={{ color: metric.color }}>{metric.val}</div>
          <div className="text-white text-[10px] font-medium">{metric.label}</div>
          <div className="text-slate-500 text-[9px]">{metric.status}</div>
        </div>

        {/* Alert badge (optional) */}
        {alert && (
          <div className="absolute bottom-12 -right-10 z-30 bg-slate-800/95 border border-slate-700
                          rounded-xl px-3 py-2 shadow-lg backdrop-blur-xl
                          flex items-center gap-2 transition-all duration-500">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px]"
                 style={{ background: slide.accent }}>
              <Zap className="w-3 h-3 text-white" />
            </div>
            <div>
              <div className="text-white text-[10px] font-semibold">{alert.text}</div>
              <div className="text-slate-400 text-[9px]">{alert.sub}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ──────────────────────────────────────────────────────── */
export function CinematicHero({
  brandName      = "MANDO",
  onCTA,
  onDemo,
}) {
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [paused, setPaused]   = useState(false);
  const timerRef              = useRef(null);
  const heroRef               = useRef(null);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
    setAnimKey(k => k + 1);
  }, []);

  const advance = useCallback(() => {
    setCurrent(c => {
      const next = (c + 1) % SLIDES.length;
      setAnimKey(k => k + 1);
      return next;
    });
  }, []);

  /* Auto-advance */
  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(advance, DURATION);
    return () => clearInterval(timerRef.current);
  }, [paused, advance]);

  /* GSAP entrance on mount — importado de forma dinámica para no bloquear LCP */
  useEffect(() => {
    if (!heroRef.current) return;
    let ctx;
    import("gsap").then(({ gsap }) => {
      if (!heroRef.current) return;
      ctx = gsap.context(() => {
        gsap.fromTo(
          ".ch-hero-enter",
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.1, delay: 0.15, clearProps: "transform" }
        );
      }, heroRef);
    });
    return () => ctx?.revert();
  }, []);

  const slide = SLIDES[current];

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      <section
        ref={heroRef}
        className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#080d1a]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        aria-label="Hero MANDO"
      >
        {/* Background */}
        <div className="absolute inset-0 ch-grid-bg pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none transition-all duration-700"
             style={{ background: `radial-gradient(ellipse 70% 50% at 50% -10%, ${slide.accent}16, transparent)` }} />
        {/* Ambient blob */}
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
             style={{ background: `${slide.accent}08`, filter: "blur(80px)", transition: "background 0.8s ease" }} />

        {/* ── MAIN GRID ── */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* ── LEFT ── */}
            <div className="space-y-7 lg:space-y-8">

              {/* Badge */}
              <div className="ch-hero-enter inline-flex items-center gap-2.5 bg-white/5 border border-white/10
                              rounded-full px-4 py-2 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full ch-pulse"
                      style={{ background: slide.accent, transition: "background 0.6s" }} />
                <span className="text-sm text-slate-300">
                  <span className="text-white font-semibold">+320 empresas</span>
                  {" · "}
                  <span className="text-yellow-400">★ 4.9/5</span>
                  {" · Sin tarjeta"}
                </span>
              </div>

              {/* Headline — key forces remount for animation */}
              <h1 key={`headline-${animKey}`} className="ch-slide-enter m-0 p-0 font-inherit">
                <div className="ch-big text-[clamp(3rem,8.5vw,6rem)] text-white">
                  {slide.line1}
                </div>
                <div className={cn(
                  "ch-big text-[clamp(3rem,8.5vw,6rem)]",
                  slide.line2cls
                )}
                  style={slide.line2cls === "ch-outline"
                    ? { WebkitTextStrokeColor: slide.accent, transition: "-webkit-text-stroke-color 0.6s" }
                    : { color: slide.accent }
                }>
                  {slide.line2}
                </div>
              </h1>

              {/* Sub-text */}
              <p key={`sub-${animKey}`} className="ch-slide-enter text-lg text-slate-400 leading-relaxed max-w-md"
                 style={{ animationDelay: "0.08s" }}>
                {slide.sub}
              </p>

              {/* Stat (slide 2 only) */}
              {slide.stat && (
                <div key={`stat-${animKey}`} className="ch-slide-enter text-sm text-slate-500 italic"
                     style={{ animationDelay: "0.12s" }}>
                  <span className="ch-shimmer-text font-black text-xl not-italic mr-2">127</span>
                  {slide.stat}
                </div>
              )}

              {/* CTAs */}
              <div className="ch-hero-enter flex flex-col sm:flex-row gap-3">
                <button
                  onClick={onCTA || (() => window.open("https://appmando.leapifyes.com/", "_blank"))}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4
                             text-white font-bold text-base rounded-lg transition-all duration-200
                             hover:scale-[1.03] shadow-lg"
                  style={{
                    background: slide.accent,
                    boxShadow: `0 8px 32px ${slide.accent}44`,
                    transition: "background 0.5s ease, box-shadow 0.5s ease",
                  }}
                >
                  Empieza gratis 15 días
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={onDemo || (() => window.open("https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000201008", "_blank"))}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4
                             border border-white/15 text-white font-medium text-base rounded-lg
                             hover:bg-white/6 hover:border-white/30 transition-all duration-200"
                >
                  Ver demo en vivo
                </button>
              </div>

              {/* Trust */}
              <div className="ch-hero-enter flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-500">
                {["Sin tarjeta", "Sin permanencia", "Soporte en español"].map(t => (
                  <span key={t} className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />{t}
                  </span>
                ))}
              </div>

              {/* ── Progress dots ── */}
              <div className="ch-hero-enter flex items-center gap-3 pt-2">
                {SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => goTo(i)}
                    aria-label={`Slide ${i + 1}`}
                    className="relative overflow-hidden rounded-full transition-all duration-300"
                    style={{
                      width:  i === current ? 32 : 8,
                      height: 8,
                      background: i === current ? slide.accent : "rgba(255,255,255,0.15)",
                    }}
                  >
                    {/* Progress fill */}
                    {i === current && !paused && (
                      <span
                        key={`prog-${animKey}`}
                        className="absolute inset-0 rounded-full ch-progress-bar origin-left"
                        style={{
                          "--ch-duration": `${DURATION}ms`,
                          background: "rgba(255,255,255,0.4)",
                        }}
                      />
                    )}
                  </button>
                ))}
                <span className="text-slate-600 text-xs ml-1 tabular-nums">
                  {current + 1} / {SLIDES.length}
                </span>
              </div>
            </div>

            {/* ── RIGHT — Mockup ── */}
            <div className="ch-hero-enter flex justify-center lg:justify-end pr-0 lg:pr-8">
              <AppMockup key={`mockup-${current}`} slide={slide} />
            </div>

          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 inset-x-0 h-28 pointer-events-none z-10"
             style={{ background: "linear-gradient(to top, #080d1a, transparent)" }} />
      </section>
    </>
  );
}

export default CinematicHero;
